import React, {useEffect, useState, useRef} from 'react';
import {Clock} from 'lucide-react';
import {useDispatch, useSelector} from 'react-redux';
import {PatientDashboardSliceInitalState} from '@/interface';
import {recentCasesSelector} from '@/services/redux/selectors/patientDashboardSelector';
import {specialistBookingRequest, followUpConsultationRequest, selectHospitalSpecialistRequest } from '@/services/redux/slices/patientDashboardSlice';
import {Button, Icon, Text} from '@/ui-kits';
import { ConsultationType } from '@/constants';
import {ReferralModal} from '@/components/consultation-screen/patient-consultation/ReferralModal';
import {cn} from '@/utils/common_functions';

type RecentCase = PatientDashboardSliceInitalState['recentCases'][number];

interface RecentDoctorCardProps {
  doctor: RecentCase;
  onAction: (doctor: RecentCase) => void;
}

const formatClosedAt = (value?: string | null): string => {
  if (!value) return '--';

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
};

const RecentDoctorCard: React.FC<RecentDoctorCardProps> = ({doctor, onAction}) => {
  const dispatch = useDispatch();
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showHospitalTooltip, setShowHospitalTooltip] = useState(false);
  const isLongHospitalName = (doctor.hospital_name?.length ?? 0) > 25;
  const hospitalTooltipRef = useRef<HTMLDivElement>(null);

  const success = useSelector((state: any) => state.patientDashboard.patientDashboardSuccess);
  useEffect(() => {
    if (success === 'S-10100' || success === 'S-10097') {
      onAction(doctor);
    }
  }, [success]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hospitalTooltipRef.current && !hospitalTooltipRef.current.contains(event.target as Node)) {
        setShowHospitalTooltip(false);
      }
    };

    if (showHospitalTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showHospitalTooltip]);

  const actionLabel = (() => {
    const now = new Date();

    // ============== Check if this case itself is already a follow-up or referral =======================
    if (
      doctor.type?.id === ConsultationType['Follow Up Second Opinion'] || 
      doctor.type?.id === ConsultationType['Referral Second Opinion'] ||
      doctor.type?.id === ConsultationType['Hospital Follow Up Second Opinion'] ||
      doctor.type?.id === ConsultationType['Hospital Referral Second Opinion']
    ) {
      return 'Consult Again';
    }

    // ============== Check if referral already created from this case =======================
    if (doctor.linked_cases?.is_referral_created) {
      return 'Consult Again';
    }

    // ============== Check if follow-up already created from this case =======================
    if (doctor.linked_cases?.is_follow_up_created) {
      return 'Consult Again';
    }

    // ============== If neither created, check expiry dates =======================
    if (doctor.referral_expiry_date && new Date(doctor.referral_expiry_date) >= now) {
      return 'Referral';
    }

    if (doctor.follow_up_date && new Date(doctor.follow_up_date) >= now) {
      return 'Follow-up';
    }

    return 'Consult Again';
  })();

  const handleBookNow = () => {
    if (actionLabel === 'Referral') {
      // Open referral modal
      setShowReferralModal(true);
      return;
    }

    if (actionLabel === 'Follow-up') {
      dispatch(followUpConsultationRequest({id: doctor.id}));
    } else {
      // Consult Again
      const typeMap: Record<string, ConsultationType> = {
        'Follow-up': ConsultationType['Follow Up Second Opinion'],
        Referral: ConsultationType['Referral Second Opinion'],
        'Consult Again': ConsultationType['Online Second Opinion'],
      };
      // For hospital specialists, use selectHospitalSpecialist flow
      if (doctor.is_hospital_specialist) {
        dispatch(selectHospitalSpecialistRequest({
          id: doctor.doctor_id
        }));
      } else {
        // For regular specialists, use regular booking flow
        dispatch(specialistBookingRequest({
          id: doctor.doctor_id,
          type: typeMap[actionLabel],
        }));
    }
    }
  };

  const handleCloseReferralModal = () => {
    setShowReferralModal(false);
  };

  return (
    <div className="bg-white rounded-2xl p-5 flex flex-col gap-4 min-w-[220px] w-[calc(33.33%-11px)] shadow-[0_0_12px_rgba(0,0,0,0.08)] border border-gray-100">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#CBEBFB] flex-shrink-0 flex items-center justify-center overflow-hidden">
          {doctor.doctor_photo && <img src={doctor.doctor_photo} alt={doctor.doctor_name} className="w-full h-full object-cover" />}
        </div>

        <div className="flex flex-col min-w-0 gap-0.5">
          <span className="text-base font-FigtreeBold font-bold text-ui_blue leading-snug line-clamp-2" style={{fontSize: '18px'}}>
            {doctor.doctor_name}
          </span>
          <span className="text-sm font-semibold font-bold gradient-text" style={{fontSize: '14px'}}>
            {doctor.specialization?.name ?? ''}
          </span>
        </div>
      </div>

      {/* Displaying Hospital Name and Address for Hospital Flow Case*/}
      {doctor.is_hospital_specialist && (doctor.hospital_name || doctor.hospital_address) && (
        <div className="relative" ref={hospitalTooltipRef}>
          {showHospitalTooltip && isLongHospitalName && (
            <div className={cn('absolute bottom-full left-0 mb-2 bg-white border-2 border-[#4DBD9F] rounded-xl px-1 py-1 z-20 w-max max-w-[260px]')}>
              <Text variant="body" className="!font-FigtreeBold !text-sm text-[#0C8363] whitespace-normal leading-tight">
                {doctor.hospital_name}
              </Text>
            </div>
          )}
          <div className={cn('flex gap-1.5 bg-[#EFFEED] border-[#4DBD9F] border border-l-4 p-1 rounded-md items-start min-w-0 overflow-hidden')}>
            <Icon type="hospital_specialist" className="flex-shrink-0 w-[30px] h-[26px] text-[#0C8363]" />
            <div className="break-words min-w-0 flex-1 text-xs text-[#0C8363]">
              <div className="flex items-center gap-1">
                <Text variant="body" className="!font-FigtreeBold line-clamp-1">
                  {doctor.hospital_name ?? ''}
                </Text>
                {isLongHospitalName && (
                  <button
                    onClick={() => setShowHospitalTooltip(prev => !prev)}
                    className={cn('flex-shrink-0 p-0.5 hover:opacity-70 transition-opacity')}
                    title="Show full hospital name">
                    <Icon type="i_icon" className="flex-shrink-0 size-3.5 text-[#0C8363]" />
                  </button>
                )}
              </div>
              <span className='flex items-center gap-1 mt-0.5'>
                <Icon type="location" className={cn('flex-shrink-0 size-3')} />
                <Text variant="tiny" className="text-ui_blue">
                  {doctor.hospital_address ?? ''}
                </Text>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Now show the Hospital Name and Address for Referral to any Hospital Specialist from Normal Regular Specialist */}
      {/* {doctor.referral && doctor.referral.data?.is_hospital_specialist && (
        <div className="flex gap-1.5 items-start min-w-2 overflow-hidden px-1 py-2 rounded-lg bg-[#EFFEF4]">
          <Icon type="hospital_specialist" className="flex-shrink-0 size-3 mt-0.5 text-[#0C8363]" />
          <span className="break-words min-w-0 flex-1 text-xs text-[#0C8363]">
            <span className="font-FigtreeBold">Referred to: {doctor.referral.data?.hospital ?? ''}</span>
            {doctor.referral.data?.address && <span className="font-FigtreeRegular">, {doctor.referral.data?.address}</span>}
          </span>
        </div>
      )} */}

      <div className="flex items-center gap-2 text-sm text-[#6B7280]">
        <Clock className="w-4 h-4 flex-shrink-0 text-[#6B7280]" />
        <span className="font-FigtreeRegular">Last consulted: {formatClosedAt(doctor.close_at)}</span>
      </div>

      <Button
        title={actionLabel}
        variant="secondary"
        size="md"
        className="w-full rounded-xl border-2 border-ui_blue py-2.5"
        textClassName="text-sm font-FigtreeBold gradient-text"
        onClick={handleBookNow}
      />

      {/* Referral Modal */}
      {showReferralModal && doctor.referral && (
        <ReferralModal
          referral={doctor.referral}
          isOpen={showReferralModal}
          onClose={handleCloseReferralModal}
          specialistName={doctor.doctor_name}
          case_id={doctor.id}
          readonly={false}
        />
      )}
    </div>
  );
};

export const RecentDoctors: React.FC<{
  onDoctorAction: (doctor: RecentCase) => void;
}> = ({onDoctorAction}) => {
  const recentCases = useSelector(recentCasesSelector) as RecentCase[];
  const doctors = recentCases.slice(0, 3);

  if (!doctors.length) return null;

  return (
    <div className="w-full max-w-[1536px] self-center mb-8">
      <h2 className="text-lg sm:text-xl font-FigtreeBold font-bold text-ui_blue mb-4">Recent Doctors</h2>

      <div className="flex flex-row gap-4 items-start overflow-x-auto pb-1">
        {doctors.map(doctor => (
          <RecentDoctorCard key={doctor.id} doctor={doctor} onAction={onDoctorAction} />
        ))}
      </div>
    </div>
  );
};

export default RecentDoctors;
